<script lang="ts">
export default {
  name: 'AppMenu',
};

interface MenuItemGroup {
  items: MenuItem[];
  hasDivider: boolean;
}

interface MenuItem {
  label: string;
  handler: () => void;
  hidden: () => boolean;
  hoverColor?: string;
}
</script>

<script setup lang="ts">
import { Menu, Settings } from 'lucide-vue-next';
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { AppState } from '@/store';
import { IsUserAClient, IsUserAnAdmin, UserHasPermission } from '@/utilities/AuthUtils';
import { UserPermission } from '@/types/UserPermissions';

const store = useStore<AppState>();
const router = useRouter();

const emit = defineEmits<{
  handleOption: [option: string];
}>();

// this needed to allow the button to toggle the menu
const isMenuOpen = ref(false);

// ---------
// computed
// ---------
const userName = computed(() => {
  return store.state.user?.name ?? '';
});

const userEmail = computed(() => {
  return store.state.user?.email ?? '';
});

const isUserAClient = computed(() => {
  return IsUserAClient(store.state.user);
});

const isUserAnAdmin = computed(() => {
  return IsUserAnAdmin(store.state.user);
});

const canViewNotifications = computed(() => {
  return isUserAnAdmin.value || UserHasPermission(store.state.user, UserPermission.NotificationsRead);
});

const menuItemGroups = ref<MenuItemGroup[]>([
  {
    items: [
      { label: 'Client Views', handler: handleClientView, hidden: () => isUserAClient.value },
      { label: 'Notifications', handler: handleNotificationsPage, hidden: () => !canViewNotifications.value },
    ],
    hasDivider: true,
  },
  {
    items: [
      { label: 'Help Center', handler: handleHelp, hidden: () => isUserAClient.value },
      { label: 'Submit Feedback', handler: handleFeedback, hidden: () => false },
      { label: 'Demo Environment', handler: handleDemoEnvironment, hidden: () => isUserAClient.value },
    ],
    hasDivider: true,
  },
  {
    items: [{ label: 'Logout', handler: handleLogout, hoverColor: 'tw:hover:bg-red-600', hidden: () => false }],
    hasDivider: false,
  },
]);

// ---------
// watchers
// ---------
watch(isMenuOpen, (isOpen) => {
  if (!isOpen) {
    blurActiveElement();
  }
});

// ----------
// functions
// ----------
function blurActiveElement() {
  const activeElement = document.activeElement as HTMLElement;
  activeElement.blur();
}

function handleProfile() {
  blurActiveElement();
  router.push('/profile');
}

function handleHelp() {
  blurActiveElement();
  window.open('https://cobbs-allen.sharefile.com/f/fodf52cc-2817-4922-bf95-8473a458c56a', '_blank');
}

function handleFeedback() {
  blurActiveElement();
  emit('handleOption', 'feedback');
}

function handleDemoEnvironment() {
  blurActiveElement();
  window.open('https://demo.platform.cacspecialty.com/', '_blank');
}

function handleClientView() {
  blurActiveElement();
  emit('handleOption', 'client-views');
}

function handleNotificationsPage() {
  blurActiveElement();
  emit('handleOption', 'notifications');
}

function handleLogout() {
  router.push('/logout');
}

function toggleMenuClick() {
  isMenuOpen.value = !isMenuOpen.value;
}

function isMenuGroupEmpty(group: MenuItemGroup): boolean {
  return group.items.every((item) => item?.hidden?.());
}
</script>

<template>
  <div class="tw:dropdown tw:dropdown-end tw:dropdown-bottom tw:mx-1 tw:h-full">
    <div
      @click="toggleMenuClick"
      @blur="isMenuOpen = false"
      tabindex="0"
      role="button"
      class="tw:btn tw:h-full tw:border-none tw:bg-primary/5 tw:btn-ghost tw:btn-sm tw:hover:bg-gray-600/30">
      <Menu :size="18" class="tw:stroke-white" />
    </div>
    <div
      tabindex="0"
      class="tw:dropdown-content tw:card-compact tw:card tw:min-w-44 tw:rounded-lg tw:bg-dark tw:pb-2 tw:text-gray-200"
      @blur="isMenuOpen = false">
      <div class="tw:card-body tw:pt-3 tw:pb-0">
        <div class="tw:m-0 tw:card-title tw:justify-between tw:gap-2">
          <div>
            <div class="tw:text-sm">{{ userName }}</div>
            <div class="tw:text-xs tw:font-medium">{{ userEmail }}</div>
          </div>
          <button
            title="Manage Profile"
            @click="handleProfile"
            class="tw:btn tw:btn-circle tw:border-white/20 tw:bg-gray-700/40 tw:shadow-none tw:btn-sm tw:hover:border-white/10 tw:hover:bg-dark">
            <Settings :size="16" class="tw:stroke-white" />
          </button>
        </div>
      </div>
      <div class="tw:divider tw:m-0 tw:divider-accent tw:px-2 tw:opacity-20"></div>

      <div v-for="(group, groupIndex) in menuItemGroups" :key="groupIndex">
        <template v-if="!isMenuGroupEmpty(group)">
          <ul class="tw:menu tw:w-full tw:gap-1 tw:py-0">
            <li v-for="(item, itemIndex) in group.items" :key="itemIndex">
              <a
                v-if="!item.hidden()"
                @click="item.handler"
                class="tw:text-base-300"
                :class="item.hoverColor ? item.hoverColor : 'tw:hover:bg-gray-700'">
                {{ item.label }}
              </a>
            </li>
          </ul>
          <div v-if="group.hasDivider" class="tw:divider tw:m-0 tw:divider-primary tw:px-2 tw:opacity-20"></div>
        </template>
      </div>
    </div>
  </div>
</template>
